import { useState, useEffect } from 'react';
import Layout from '../../../components/layout/Layout';
import VoucherSendModal from '../../../components/vouchers/VoucherSendModal';
import AdminPixPagamentoCard from './components/AdminPixPagamentoCard';
import AdminFinancialCard from './components/AdminFinancialCard';

interface Reward {
  id: number;
  name: string;
  description: string;
  image: string;
  category: string;
  points: number;
  stock: number;
  claimed: number;
  available: boolean;
  featured: boolean;
  expiresAt?: string;
  price?: number; // Preço em pontos para vouchers
  adminFee?: number; // Taxa administrativa em %
}

interface ClaimedReward {
  id: number;
  rewardId: number;
  rewardName: string;
  rewardImage: string;
  points: number;
  claimedDate: string;
  status: 'pendente' | 'processando' | 'enviado' | 'entregue' | 'cancelado';
  trackingCode?: string;
}

interface Campaign {
  id: number;
  name: string;
  status: 'ativa' | 'pausada' | 'finalizada';
  metas: Meta[];
  gamificacoes: Gamificacao[];
}

interface Meta {
  id: number;
  nome: string;
  descricao: string;
  tipo: 'vendas' | 'performance' | 'aquisicao' | 'capacitacao';
  status: 'ativa' | 'pausada' | 'concluida';
}

interface Gamificacao {
  id: number;
  nome: string;
  descricao: string;
  tipo: 'missao' | 'ranking' | 'distintivo' | 'desafio';
  status: 'ativa' | 'pausada' | 'concluida';
}

interface CartItem {
  id: number;
  name: string;
  price: number; // Preço em pontos
  quantity: number;
  image: string;
  adminFee: number; // Taxa administrativa
}

interface Purchase {
  id: number;
  date: string;
  items: CartItem[];
  total: number; // Total em pontos
  status: 'concluida' | 'processando' | 'cancelada';
  invoiceNumber: string;
  debitNoteNumber: string;
  paymentMethod: string;
}

export default function RecompensasPage() {
  const [activeTab, setActiveTab] = useState<'produtos' | 'vouchers' | 'experiencias' | 'financeiras' | 'cursos'>('produtos');
  const [selectedCategory, setSelectedCategory] = useState('todos');
  const [selectedReward, setSelectedReward] = useState<Reward | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<number | null>(null);
  const [selectedCards, setSelectedCards] = useState<Set<number>>(new Set());
  const [showVinculationSuccess, setShowVinculationSuccess] = useState(false);
  const [showCart, setShowCart] = useState(false);
  const [showPurchaseReport, setShowPurchaseReport] = useState(false);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [showCheckout, setShowCheckout] = useState(false);
  const [showVinculationModal, setShowVinculationModal] = useState(false);
  const [vinculationType, setVinculationType] = useState<'meta' | 'gamificacao' | 'produto'>('produto');
  const [selectedMeta, setSelectedMeta] = useState<number | null>(null);
  const [selectedGamificacao, setSelectedGamificacao] = useState<number | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<'pix' | 'boleto'>('pix');
  const [awaitingPayment, setAwaitingPayment] = useState(false);
  const [taxaAdministrativa, setTaxaAdministrativa] = useState<number>(5.0);
  const [showVoucherSendModal, setShowVoucherSendModal] = useState(false);
  const [selectedPurchase, setSelectedPurchase] = useState<Purchase | null>(null);
  const [showVoucherSuccess, setShowVoucherSuccess] = useState(false);

  // Estados para toggles dos cards financeiros (admin) — REMOVIDO, agora usa selectedCards
  // const [financialPixPagamentoEnabled, setFinancialPixPagamentoEnabled] = useState(false);
  // const [financialCashbackEnabled, setFinancialCashbackEnabled] = useState(false);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(12);

  const userPoints = 2450;

  // Effect para redirecionar após 5 segundos quando aguardando pagamento PIX
  useEffect(() => {
    if (awaitingPayment && paymentMethod === 'pix') {
      const timer = setTimeout(() => {
        setAwaitingPayment(false);
        setCart([]);
        setShowCheckout(false);
        setShowSuccessModal(true);
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [awaitingPayment, paymentMethod]);

  // Campanhas disponíveis com metas e gamificações
  const campaigns: Campaign[] = [
    { 
      id: 1, 
      name: 'Campanha Vendas Q1 2024', 
      status: 'ativa',
      metas: [
        { id: 1, nome: 'Meta de Vendas Mensais', descricao: 'Atingir R$ 50.000 em vendas mensais', tipo: 'vendas', status: 'ativa' },
        { id: 2, nome: 'Novos Clientes', descricao: 'Captar 20 novos clientes por mês', tipo: 'aquisicao', status: 'ativa' },
        { id: 3, nome: 'Performance Individual', descricao: 'Superar 95% da meta individual', tipo: 'performance', status: 'ativa' }
      ],
      gamificacoes: [
        { id: 1, nome: 'Missão Vendedor do Mês', descricao: 'Complete todas as vendas do mês', tipo: 'missao', status: 'ativa' },
        { id: 2, nome: 'Ranking de Vendas', descricao: 'Competição entre vendedores', tipo: 'ranking', status: 'ativa' },
        { id: 3, nome: 'Distintivo Ouro', descricao: 'Conquiste o distintivo de excelência', tipo: 'distintivo', status: 'ativa' }
      ]
    },
    { 
      id: 2, 
      name: 'Desafio Atendimento Excelente', 
      status: 'ativa',
      metas: [
        { id: 4, nome: 'Satisfação do Cliente', descricao: 'Manter NPS acima de 8.5', tipo: 'performance', status: 'ativa' },
        { id: 5, nome: 'Tempo de Resposta', descricao: 'Responder em até 2 horas', tipo: 'performance', status: 'ativa' }
      ],
      gamificacoes: [
        { id: 4, nome: 'Desafio 5 Estrelas', descricao: 'Receba 10 avaliações 5 estrelas', tipo: 'desafio', status: 'ativa' },
        { id: 5, nome: 'Ranking Atendimento', descricao: 'Melhor atendimento do mês', tipo: 'ranking', status: 'ativa' }
      ]
    },
    { 
      id: 3, 
      name: 'Meta Trimestral', 
      status: 'ativa',
      metas: [
        { id: 6, nome: 'Receita Trimestral', descricao: 'Atingir R$ 150.000 no trimestre', tipo: 'vendas', status: 'ativa' },
        { id: 7, nome: 'Expansão de Mercado', descricao: 'Entrar em 3 novos mercados', tipo: 'aquisicao', status: 'ativa' }
      ],
      gamificacoes: [
        { id: 6, nome: 'Missão Trimestral', descricao: 'Complete todos os objetivos do trimestre', tipo: 'missao', status: 'ativa' },
        { id: 7, nome: 'Distintivo Trimestre', descricao: 'Conquiste o distintivo trimestral', tipo: 'distintivo', status: 'ativa' }
      ]
    },
    { 
      id: 4, 
      name: 'Programa Capacitação 2024', 
      status: 'ativa',
      metas: [
        { id: 8, nome: 'Cursos Concluídos', descricao: 'Complete 5 cursos de capacitação', tipo: 'capacitacao', status: 'ativa' },
        { id: 9, nome: 'Certificações', descricao: 'Obtenha 2 certificações profissionais', tipo: 'capacitacao', status: 'ativa' }
      ],
      gamificacoes: [
        { id: 8, nome: 'Missão Aprendizado', descricao: 'Complete todos os módulos de treinamento', tipo: 'missao', status: 'ativa' },
        { id: 9, nome: 'Ranking Conhecimento', descricao: 'Competição de conhecimento', tipo: 'ranking', status: 'ativa' }
      ]
    },
    { 
      id: 5, 
      name: 'Campanha Black Friday', 
      status: 'pausada',
      metas: [
        { id: 10, nome: 'Vendas Black Friday', descricao: 'Superar vendas do ano anterior', tipo: 'vendas', status: 'pausada' }
      ],
      gamificacoes: [
        { id: 10, nome: 'Desafio Black Friday', descricao: 'Venda mais que no ano passado', tipo: 'desafio', status: 'pausada' }
      ]
    },
    { 
      id: 6, 
      name: 'Incentivo Fim de Ano', 
      status: 'ativa',
      metas: [
        { id: 11, nome: 'Fechamento Anual', descricao: 'Atingir meta anual de vendas', tipo: 'vendas', status: 'ativa' },
        { id: 12, nome: 'Retenção de Clientes', descricao: 'Manter 90% dos clientes ativos', tipo: 'performance', status: 'ativa' }
      ],
      gamificacoes: [
        { id: 11, nome: 'Missão Fim de Ano', descricao: 'Complete todos os objetivos anuais', tipo: 'missao', status: 'ativa' },
        { id: 12, nome: 'Distintivo Anual', descricao: 'Conquiste o distintivo do ano', tipo: 'distintivo', status: 'ativa' }
      ]
    }
  ];

  // Histórico de compras
  const purchases: Purchase[] = [
    {
      id: 1,
      date: '2024-01-15',
      items: [
        { id: 101, name: 'Vale-Presente Alimentação R$ 50', price: 500, quantity: 2, image: 'https://readdy.ai/api/search-image?query=food%20delivery%20gift%20card%20voucher%20on%20clean%20white%20background%20with%20restaurant%20and%20food%20icons%2C%20professional%20product%20photography%2C%20high%20quality%2C%20centered%20composition&width=400&height=300&seq=101&orientation=landscape', adminFee: 5.0 },
        { id: 102, name: 'Vale-Presente Vestuário R$ 100', price: 900, quantity: 1, image: 'https://readdy.ai/api/search-image?query=clothing%20fashion%20gift%20card%20voucher%20on%20clean%20white%20background%20with%20clothing%20and%20fashion%20icons%2C%20professional%20product%20photography%2C%20high%20quality%2C%20centered%20composition&width=400&height=300&seq=102&orientation=landscape', adminFee: 5.0 }
      ],
      total: 1900,
      status: 'concluida',
      invoiceNumber: 'NF-2024-001',
      debitNoteNumber: 'ND-2024-001',
      paymentMethod: 'Cartão de Crédito'
    },
    {
      id: 2,
      date: '2024-01-20',
      items: [
        { id: 103, name: 'Vale-Presente Entretenimento R$ 120', price: 1200, quantity: 1, image: 'https://readdy.ai/api/search-image?query=entertainment%20streaming%20gift%20card%20voucher%20on%20clean%20white%20background%20with%20movie%20and%20music%20icons%2C%20professional%20product%20photography%2C%20high%20quality%2C%20centered%20composition&width=400&height=300&seq=103&orientation=landscape', adminFee: 4.5 }
      ],
      total: 1254,
      status: 'concluida',
      invoiceNumber: 'NF-2024-002',
      debitNoteNumber: 'ND-2024-002',
      paymentMethod: 'PIX'
    },
    {
      id: 3,
      date: '2024-01-25',
      items: [
        { id: 105, name: 'Vale-Presente Transporte R$ 75', price: 700, quantity: 3, image: 'https://readdy.ai/api/search-image?query=transportation%20mobility%20gift%20card%20voucher%20on%20clean%20white%20background%20with%20car%20and%20transport%20icons%2C%20professional%20product%20photography%2C%20high%20quality%2C%20centered%20composition&width=400&height=300&seq=105&orientation=landscape', adminFee: 5.5 }
      ],
      total: 2215,
      status: 'processando',
      invoiceNumber: 'NF-2024-003',
      debitNoteNumber: 'ND-2024-003',
      paymentMethod: 'Cartão de Débito'
    }
  ];

  const rewards: Reward[] = [
    {
      id: 1,
      name: 'Smartphone Samsung Galaxy A54',
      description: 'Smartphone com tela de 6.4", 128GB, câmera tripla 50MP',
      image: 'https://readdy.ai/api/search-image?query=modern%20smartphone%20samsung%20galaxy%20on%20clean%20white%20background%2C%20professional%20product%20photography%2C%20high%20quality%2C%20centered%20composition&width=400&height=300&seq=1&orientation=landscape',
      category: 'eletronicos',
      points: 2500,
      stock: 15,
      claimed: 8,
      available: true,
      featured: true
    },
    {
      id: 2,
      name: 'Fone de Ouvido Bluetooth JBL',
      description: 'Fone wireless com cancelamento de ruído e 30h de bateria',
      image: 'https://readdy.ai/api/search-image?query=wireless%20bluetooth%20headphones%20JBL%20on%20clean%20white%20background%2C%20professional%20product%20photography%2C%20high%20quality%2C%20centered%20composition&width=400&height=300&seq=2&orientation=landscape',
      category: 'eletronicos',
      points: 800,
      stock: 25,
      claimed: 12,
      available: true,
      featured: false
    },
    {
      id: 3,
      name: 'Cafeteira Nespresso',
      description: 'Máquina de café expresso automática com 19 bar de pressão',
      image: 'https://readdy.ai/api/search-image?query=nespresso%20coffee%20machine%20on%20clean%20white%20background%2C%20professional%20product%20photography%2C%20high%20quality%2C%20centered%20composition&width=400&height=300&seq=3&orientation=landscape',
      category: 'casa',
      points: 1200,
      stock: 10,
      claimed: 5,
      available: true,
      featured: true
    },
    {
      id: 4,
      name: 'Kit Churrasco Premium',
      description: 'Conjunto completo com 12 peças para churrasco',
      image: 'https://readdy.ai/api/search-image?query=premium%20barbecue%20grill%20set%20tools%20on%20clean%20white%20background%2C%20professional%20product%20photography%2C%20high%20quality%2C%20centered%20composition&width=400&height=300&seq=4&orientation=landscape',
      category: 'casa',
      points: 600,
      stock: 20,
      claimed: 15,
      available: true,
      featured: false
    },
    {
      id: 5,
      name: 'Tênis Nike Air Max',
      description: 'Tênis esportivo com tecnologia Air Max e design moderno',
      image: 'https://readdy.ai/api/search-image?query=nike%20air%20max%20sneakers%20on%20clean%20white%20background%2C%20professional%20product%20photography%2C%20high%20quality%2C%20centered%20composition&width=400&height=300&seq=5&orientation=landscape',
      category: 'moda',
      points: 900,
      stock: 30,
      claimed: 18,
      available: true,
      featured: true
    },
    {
      id: 6,
      name: 'Mochila Executiva',
      description: 'Mochila para notebook até 15.6" com compartimentos organizadores',
      image: 'https://readdy.ai/api/search-image?query=executive%20laptop%20backpack%20on%20clean%20white%20background%2C%20professional%20product%20photography%2C%20high%20quality%2C%20centered%20composition&width=400&height=300&seq=6&orientation=landscape',
      category: 'moda',
      points: 400,
      stock: 40,
      claimed: 22,
      available: true,
      featured: false
    },
    {
      id: 7,
      name: 'Tablet Samsung Galaxy Tab A8',
      description: 'Tablet 10.5" com 64GB, ideal para trabalho e entretenimento',
      image: 'https://readdy.ai/api/search-image?query=samsung%20galaxy%20tablet%20on%20clean%20white%20background%2C%20professional%20product%20photography%2C%20high%20quality%2C%20centered%20composition&width=400&height=300&seq=7&orientation=landscape',
      category: 'eletronicos',
      points: 1800,
      stock: 12,
      claimed: 6,
      available: true,
      featured: true
    },
    {
      id: 8,
      name: 'Air Fryer Philips Walita',
      description: 'Fritadeira elétrica sem óleo, 4.1L, timer e controle de temperatura',
      image: 'https://readdy.ai/api/search-image?query=philips%20air%20fryer%20on%20clean%20white%20background%2C%20professional%20product%20photography%2C%20high%20quality%2C%20centered%20composition&width=400&height=300&seq=8&orientation=landscape',
      category: 'casa',
      points: 700,
      stock: 18,
      claimed: 9,
      available: true,
      featured: false
    },
    {
      id: 9,
      name: 'Relógio Smartwatch',
      description: 'Smartwatch com monitor cardíaco, GPS e resistente à água',
      image: 'https://readdy.ai/api/search-image?query=modern%20smartwatch%20on%20clean%20white%20background%2C%20professional%20product%20photography%2C%20high%20quality%2C%20centered%20composition&width=400&height=300&seq=9&orientation=landscape',
      category: 'eletronicos',
      points: 1100,
      stock: 22,
      claimed: 14,
      available: true,
      featured: true
    },
    {
      id: 10,
      name: 'Jogo de Panelas Antiaderente',
      description: 'Conjunto com 5 panelas antiaderentes e tampas de vidro',
      image: 'https://readdy.ai/api/search-image?query=non-stick%20cookware%20set%20pans%20on%20clean%20white%20background%2C%20professional%20product%20photography%2C%20high%20quality%2C%20centered%20composition&width=400&height=300&seq=10&orientation=landscape',
      category: 'casa',
      points: 500,
      stock: 35,
      claimed: 20,
      available: true,
      featured: false
    },
    {
      id: 11,
      name: 'Camiseta Polo Lacoste',
      description: 'Camiseta polo clássica 100% algodão, disponível em várias cores',
      image: 'https://readdy.ai/api/search-image?query=lacoste%20polo%20shirt%20on%20clean%20white%20background%2C%20professional%20product%20photography%2C%20high%20quality%2C%20centered%20composition&width=400&height=300&seq=11&orientation=landscape',
      category: 'moda',
      points: 350,
      stock: 50,
      claimed: 28,
      available: true,
      featured: false
    },
    {
      id: 12,
      name: 'Mouse Gamer Logitech',
      description: 'Mouse gamer com sensor óptico de alta precisão e RGB',
      image: 'https://readdy.ai/api/search-image?query=logitech%20gaming%20mouse%20with%20RGB%20lighting%20on%20clean%20white%20background%2C%20professional%20product%20photography%2C%20high%20quality%2C%20centered%20composition&width=400&height=300&seq=12&orientation=landscape',
      category: 'eletronicos',
      points: 300,
      stock: 45,
      claimed: 25,
      available: true,
      featured: false
    },
    {
      id: 13,
      name: 'Perfume Importado 100ml',
      description: 'Fragrância masculina sofisticada com notas amadeiradas',
      image: 'https://readdy.ai/api/search-image?query=luxury%20mens%20perfume%20bottle%20on%20clean%20white%20background%2C%20professional%20product%20photography%2C%20high%20quality%2C%20centered%20composition&width=400&height=300&seq=13&orientation=landscape',
      category: 'beleza',
      points: 450,
      stock: 28,
      claimed: 16,
      available: true,
      featured: false
    },
    {
      id: 14,
      name: 'Kit Skincare Facial',
      description: 'Kit completo para cuidados faciais com 4 produtos',
      image: 'https://readdy.ai/api/search-image?query=facial%20skincare%20kit%20products%20on%20clean%20white%20background%2C%20professional%20product%20photography%2C%20high%20quality%2C%20centered%20composition&width=400&height=300&seq=14&orientation=landscape',
      category: 'beleza',
      points: 380,
      stock: 32,
      claimed: 19,
      available: true,
      featured: false
    },
    {
      id: 15,
      name: 'Cadeira Gamer RGB',
      description: 'Cadeira ergonômica para games com iluminação RGB e apoio lombar',
      image: 'https://readdy.ai/api/search-image?query=RGB%20gaming%20chair%20ergonomic%20on%20clean%20white%20background%2C%20professional%20product%20photography%2C%20high%20quality%2C%20centered%20composition&width=400&height=300&seq=15&orientation=landscape',
      category: 'eletronicos',
      points: 2200,
      stock: 8,
      claimed: 3,
      available: true,
      featured: true
    },
    {
      id: 16,
      name: 'Conjunto de Toalhas Premium',
      description: 'Kit com 6 toalhas 100% algodão egípcio, macias e absorventes',
      image: 'https://readdy.ai/api/search-image?query=premium%20cotton%20towel%20set%20on%20clean%20white%20background%2C%20professional%20product%20photography%2C%20high%20quality%2C%20centered%20composition&width=400&height=300&seq=16&orientation=landscape',
      category: 'casa',
      points: 280,
      stock: 60,
      claimed: 35,
      available: true,
      featured: false
    }
  ];

  const vouchers: Reward[] = [
    {
      id: 101,
      name: 'Vale-Presente Alimentação R$ 50',
      description: 'Cartão presente digital para usar em restaurantes e delivery',
      image: 'https://readdy.ai/api/search-image?query=food%20delivery%20gift%20card%20voucher%20on%20clean%20white%20background%20with%20restaurant%20and%20food%20icons%2C%20professional%20product%20photography%2C%20high%20quality%2C%20centered%20composition&width=400&height=300&seq=101&orientation=landscape',
      category: 'alimentacao',
      points: 500,
      stock: 100,
      claimed: 45,
      available: true,
      featured: true,
      expiresAt: '2024-12-31',
      price: 500,
      adminFee: 5.0
    },
    {
      id: 102,
      name: 'Vale-Presente Vestuário R$ 100',
      description: 'Crédito para compras em lojas de roupas e acessórios',
      image: 'https://readdy.ai/api/search-image?query=clothing%20fashion%20gift%20card%20voucher%20on%20clean%20white%20background%20with%20clothing%20and%20fashion%20icons%2C%20professional%20product%20photography%2C%20high%20quality%2C%20centered%20composition&width=400&height=300&seq=102&orientation=landscape',
      category: 'vestuario',
      points: 900,
      stock: 80,
      claimed: 38,
      available: true,
      featured: true,
      expiresAt: '2024-12-31',
      price: 900,
      adminFee: 5.0
    },
    {
      id: 103,
      name: 'Vale-Presente Entretenimento R$ 120',
      description: 'Crédito para streaming, cinema e entretenimento',
      image: 'https://readdy.ai/api/search-image?query=entertainment%20streaming%20gift%20card%20voucher%20on%20clean%20white%20background%20with%20movie%20and%20music%20icons%2C%20professional%20product%20photography%2C%20high%20quality%2C%20centered%20composition&width=400&height=300&seq=103&orientation=landscape',
      category: 'entretenimento',
      points: 1200,
      stock: 50,
      claimed: 22,
      available: true,
      featured: true,
      expiresAt: '2024-12-31',
      price: 1200,
      adminFee: 4.5
    },
    {
      id: 104,
      name: 'Vale-Presente Brinquedos R$ 80',
      description: 'Crédito para compras em lojas de brinquedos e jogos',
      image: 'https://readdy.ai/api/search-image?query=toys%20and%20games%20gift%20card%20voucher%20on%20clean%20white%20background%20with%20toy%20and%20game%20icons%2C%20professional%20product%20photography%2C%20high%20quality%2C%20centered%20composition&width=400&height=300&seq=104&orientation=landscape',
      category: 'brinquedos',
      points: 800,
      stock: 60,
      claimed: 28,
      available: true,
      featured: false,
      expiresAt: '2024-12-31',
      price: 800,
      adminFee: 6.0
    },
    {
      id: 105,
      name: 'Vale-Presente Transporte R$ 75',
      description: 'Crédito para corridas, transporte público e mobilidade',
      image: 'https://readdy.ai/api/search-image?query=transportation%20mobility%20gift%20card%20voucher%20on%20clean%20white%20background%20with%20car%20and%20transport%20icons%2C%20professional%20product%20photography%2C%20high%20quality%2C%20centered%20composition&width=400&height=300&seq=105&orientation=landscape',
      category: 'transporte',
      points: 700,
      stock: 70,
      claimed: 32,
      available: true,
      featured: false,
      expiresAt: '2024-12-31',
      price: 700,
      adminFee: 5.5
    },
    {
      id: 106,
      name: 'Vale-Presente Tecnologia R$ 50',
      description: 'Crédito para apps, softwares e produtos tecnológicos',
      image: 'https://readdy.ai/api/search-image?query=technology%20apps%20gift%20card%20voucher%20on%20clean%20white%20background%20with%20tech%20and%20software%20icons%2C%20professional%20product%20photography%2C%20high%20quality%2C%20centered%20composition&width=400&height=300&seq=106&orientation=landscape',
      category: 'tecnologia',
      points: 500,
      stock: 90,
      claimed: 41,
      available: true,
      featured: false,
      expiresAt: '2024-12-31',
      price: 500,
      adminFee: 5.0
    },
    {
      id: 107,
      name: 'Vale-Presente Varejo R$ 200',
      description: 'Cartão presente para usar em grandes redes de varejo',
      image: 'https://readdy.ai/api/search-image?query=retail%20shopping%20gift%20card%20voucher%20on%20clean%20white%20background%20with%20shopping%20and%20store%20icons%2C%20professional%20product%20photography%2C%20high%20quality%2C%20centered%20composition&width=400&height=300&seq=107&orientation=landscape',
      category: 'varejo',
      points: 1800,
      stock: 40,
      claimed: 18,
      available: true,
      featured: true,
      expiresAt: '2024-12-31',
      price: 1800,
      adminFee: 4.0
    },
    {
      id: 108,
      name: 'Vale-Presente Games R$ 100',
      description: 'Crédito para jogos digitais e plataformas de games',
      image: 'https://readdy.ai/api/search-image?query=gaming%20digital%20games%20gift%20card%20voucher%20on%20clean%20white%20background%20with%20gaming%20and%20controller%20icons%2C%20professional%20product%20photography%2C%20high%20quality%2C%20centered%20composition&width=400&height=300&seq=108&orientation=landscape',
      category: 'games',
      points: 950,
      stock: 55,
      claimed: 26,
      available: true,
      featured: false,
      expiresAt: '2024-12-31',
      price: 950,
      adminFee: 5.5
    },
    {
      id: 109,
      name: 'Vale-Presente Beleza R$ 90',
      description: 'Crédito para produtos de beleza e cuidados pessoais',
      image: 'https://readdy.ai/api/search-image?query=beauty%20cosmetics%20gift%20card%20voucher%20on%20clean%20white%20background%20with%20beauty%20and%20cosmetic%20icons%2C%20professional%20product%20photography%2C%20high%20quality%2C%20centered%20composition&width=400&height=300&seq=109&orientation=landscape',
      category: 'beleza',
      points: 850,
      stock: 65,
      claimed: 30,
      available: true,
      featured: false,
      expiresAt: '2024-12-31',
      price: 850,
      adminFee: 5.5
    },
    {
      id: 110,
      name: 'Vale-Presente Esportes R$ 120',
      description: 'Crédito para artigos esportivos e atividades físicas',
      image: 'https://readdy.ai/api/search-image?query=sports%20fitness%20gift%20card%20voucher%20on%20clean%20white%20background%20with%20sports%20and%20fitness%20icons%2C%20professional%20product%20photography%2C%20high%20quality%2C%20centered%20composition&width=400&height=300&seq=110&orientation=landscape',
      category: 'esportes',
      points: 1100,
      stock: 45,
      claimed: 20,
      available: true,
      featured: true,
      expiresAt: '2024-12-31',
      price: 1100,
      adminFee: 4.5
    },
    {
      id: 111,
      name: 'Vale-Presente Casa R$ 150',
      description: 'Crédito para produtos de casa, decoração e utilidades',
      image: 'https://readdy.ai/api/search-image?query=home%20decor%20gift%20card%20voucher%20on%20clean%20white%20background%20with%20home%20and%20furniture%20icons%2C%20professional%20product%20photography%2C%20high%20quality%2C%20centered%20composition&width=400&height=300&seq=111&orientation=landscape',
      category: 'casa',
      points: 1400,
      stock: 35,
      claimed: 15,
      available: true,
      featured: false,
      expiresAt: '2024-12-31',
      price: 1400,
      adminFee: 4.0
    },
    {
      id: 112,
      name: 'Vale-Presente Livros R$ 60',
      description: 'Crédito para livros físicos e digitais',
      image: 'https://readdy.ai/api/search-image?query=books%20education%20gift%20card%20voucher%20on%20clean%20white%20background%20with%20book%20and%20education%20icons%2C%20professional%20product%20photography%2C%20high%20quality%2C%20centered%20composition&width=400&height=300&seq=112&orientation=landscape',
      category: 'livros',
      points: 600,
      stock: 75,
      claimed: 35,
      available: true,
      featured: false,
      expiresAt: '2024-12-31',
      price: 600,
      adminFee: 5.0
    }
  ];

  const experiencias: Reward[] = [
    {
      id: 201,
      name: 'Spa Day Completo',
      description: 'Dia completo de relaxamento com massagens, sauna e tratamentos',
      image: 'https://readdy.ai/api/search-image?query=luxury%20spa%20day%20experience%20with%20massage%20stones%20and%20candles%2C%20serene%20atmosphere%2C%20professional%20photography%2C%20high%20quality%2C%20centered%20composition&width=400&height=300&seq=201&orientation=landscape',
      category: 'experiencia',
      points: 2500,
      stock: 20,
      claimed: 8,
      available: true,
      featured: true,
      expiresAt: '2024-12-31'
    },
    {
      id: 202,
      name: 'Jantar Romântico para 2',
      description: 'Jantar especial em restaurante premium com vista panorâmica',
      image: 'https://readdy.ai/api/search-image?query=romantic%20dinner%20for%20two%20at%20elegant%20restaurant%20with%20candles%20and%20city%20view%2C%20professional%20photography%2C%20high%20quality%2C%20centered%20composition&width=400&height=300&seq=202&orientation=landscape',
      category: 'experiencia',
      points: 1800,
      stock: 15,
      claimed: 6,
      available: true,
      featured: true,
      expiresAt: '2024-12-31'
    },
    {
      id: 203,
      name: 'Aula de Culinária Gourmet',
      description: 'Workshop de culinária com chef renomado, inclui degustação',
      image: 'https://readdy.ai/api/search-image?query=gourmet%20cooking%20class%20with%20professional%20chef%20in%20modern%20kitchen%2C%20hands%20preparing%20food%2C%20professional%20photography%2C%20high%20quality%2C%20centered%20composition&width=400&height=300&seq=203&orientation=landscape',
      category: 'experiencia',
      points: 1200,
      stock: 25,
      claimed: 12,
      available: true,
      featured: false,
      expiresAt: '2024-12-31'
    },
    {
      id: 204,
      name: 'Passeio de Balão',
      description: 'Voo de balão ao nascer do sol com café da manhã incluso',
      image: 'https://readdy.ai/api/search-image?query=hot%20air%20balloon%20ride%20at%20sunrise%20over%20beautiful%20landscape%2C%20peaceful%20morning%20scene%2C%20professional%20photography%2C%20high%20quality%2C%20centered%20composition&width=400&height=300&seq=204&orientation=landscape',
      category: 'experiencia',
      points: 3500,
      stock: 10,
      claimed: 3,
      available: true,
      featured: true,
      expiresAt: '2024-12-31'
    },
    {
      id: 205,
      name: 'Day Use Resort',
      description: 'Dia completo em resort com piscina, spa e refeições inclusas',
      image: 'https://readdy.ai/api/search-image?query=luxury%20resort%20day%20use%20with%20swimming%20pool%20and%20palm%20trees%2C%20tropical%20paradise%2C%20professional%20photography%2C%20high%20quality%2C%20centered%20composition&width=400&height=300&seq=205&orientation=landscape',
      category: 'experiencia',
      points: 1500,
      stock: 30,
      claimed: 15,
      available: true,
      featured: false,
      expiresAt: '2024-12-31'
    },
    {
      id: 206,
      name: 'Experiência de Vinhos',
      description: 'Degustação guiada de vinhos premium com sommelier',
      image: 'https://readdy.ai/api/search-image?query=wine%20tasting%20experience%20with%20sommelier%20in%20elegant%20cellar%2C%20wine%20glasses%20and%20bottles%2C%20professional%20photography%2C%20high%20quality%2C%20centered%20composition&width=400&height=300&seq=206&orientation=landscape',
      category: 'experiencia',
      points: 2000,
      stock: 18,
      claimed: 9,
      available: true,
      featured: true,
      expiresAt: '2024-12-31'
    },
    {
      id: 207,
      name: 'Aventura de Rafting',
      description: 'Descida de rafting em corredeiras com guia especializado',
      image: 'https://readdy.ai/api/search-image?query=white%20water%20rafting%20adventure%20with%20team%20in%20rapids%2C%20exciting%20outdoor%20activity%2C%20professional%20photography%2C%20high%20quality%2C%20centered%20composition&width=400&height=300&seq=207&orientation=landscape',
      category: 'experiencia',
      points: 1600,
      stock: 22,
      claimed: 11,
      available: true,
      featured: false,
      expiresAt: '2024-12-31'
    },
    {
      id: 208,
      name: 'Show Musical VIP',
      description: 'Ingresso VIP para show musical com camarote e open bar',
      image: 'https://readdy.ai/api/search-image?query=VIP%20concert%20experience%20with%20stage%20lights%20and%20crowd%2C%20premium%20seating%20area%2C%20professional%20photography%2C%20high%20quality%2C%20centered%20composition&width=400&height=300&seq=208&orientation=landscape',
      category: 'experiencia',
      points: 2800,
      stock: 12,
      claimed: 5,
      available: true,
      featured: true,
      expiresAt: '2024-12-31'
    }
  ];

  const financeiras: Reward[] = [
    {
      id: 301,
      name: 'Cashback R$ 100',
      description: 'Dinheiro de volta direto na sua conta corrente',
      image: 'https://readdy.ai/api/search-image?query=cashback%20money%20return%20concept%20with%20brazilian%20real%20bills%20and%20coins%2C%20financial%20reward%2C%20professional%20photography%2C%20high%20quality%2C%20centered%20composition&width=400&height=300&seq=301&orientation=landscape',
      category: 'financeira',
      points: 1000,
      stock: 200,
      claimed: 85,
      available: true,
      featured: true
    },
    {
      id: 302,
      name: 'Cashback R$ 250',
      description: 'Dinheiro de volta direto na sua conta corrente',
      image: 'https://readdy.ai/api/search-image?query=cashback%20money%20return%20concept%20with%20brazilian%20real%20bills%2C%20financial%20reward%2C%20professional%20photography%2C%20high%20quality%2C%20centered%20composition&width=400&height=300&seq=302&orientation=landscape',
      category: 'financeira',
      points: 2400,
      stock: 150,
      claimed: 62,
      available: true,
      featured: true
    },
    {
      id: 303,
      name: 'Cashback R$ 500',
      description: 'Dinheiro de volta direto na sua conta corrente',
      image: 'https://readdy.ai/api/search-image?query=cashback%20money%20return%20concept%20with%20stack%20of%20brazilian%20real%20bills%2C%20financial%20reward%2C%20professional%20photography%2C%20high%20quality%2C%20centered%20composition&width=400&height=300&seq=303&orientation=landscape',
      category: 'financeira',
      points: 4800,
      stock: 100,
      claimed: 38,
      available: true,
      featured: true
    },
    {
      id: 304,
      name: 'Vale-Combustível R$ 150',
      description: 'Crédito para abastecimento em postos conveniados',
      image: 'https://readdy.ai/api/search-image?query=fuel%20voucher%20card%20at%20gas%20station%20pump%2C%20automotive%20financial%20benefit%2C%20professional%20photography%2C%20high%20quality%2C%20centered%20composition&width=400&height=300&seq=304&orientation=landscape',
      category: 'financeira',
      points: 1500,
      stock: 120,
      claimed: 54,
      available: true,
      featured: false
    },
    {
      id: 305,
      name: 'Cartão Pré-Pago R$ 200',
      description: 'Cartão pré-pago para uso em estabelecimentos credenciados',
      image: 'https://readdy.ai/api/search-image?query=prepaid%20credit%20card%20on%20clean%20white%20background%2C%20financial%20product%2C%20professional%20photography%2C%20high%20quality%2C%20centered%20composition&width=400&height=300&seq=305&orientation=landscape',
      category: 'financeira',
      points: 2000,
      stock: 80,
      claimed: 35,
      available: true,
      featured: false
    },
    {
      id: 306,
      name: 'PIX Instantâneo R$ 300',
      description: 'Transferência PIX imediata para sua conta bancária',
      image: 'https://readdy.ai/api/search-image?query=PIX%20instant%20payment%20concept%20with%20smartphone%20and%20brazilian%20real%20money%2C%20digital%20financial%20transfer%2C%20professional%20photography%2C%20high%20quality%2C%20centered%20composition&width=400&height=300&seq=306&orientation=landscape',
      category: 'financeira',
      points: 3000,
      stock: 90,
      claimed: 42,
      available: true,
      featured: true
    },
    {
      id: 307,
      name: 'Vale-Alimentação R$ 180',
      description: 'Cartão vale-alimentação para supermercados e restaurantes',
      image: 'https://readdy.ai/api/search-image?query=meal%20voucher%20card%20with%20grocery%20items%20and%20restaurant%20symbols%2C%20food%20benefit%2C%20professional%20photography%2C%20high%20quality%2C%20centered%20composition&width=400&height=300&seq=307&orientation=landscape',
      category: 'financeira',
      points: 1800,
      stock: 110,
      claimed: 48,
      available: true,
      featured: false
    },
    {
      id: 308,
      name: 'Investimento CDB R$ 1000',
      description: 'Aplicação em CDB com rendimento de 100% do CDI',
      image: 'https://readdy.ai/api/search-image?query=investment%20CDB%20certificate%20with%20financial%20growth%20chart%2C%20banking%20product%2C%20professional%20photography%2C%20high%20quality%2C%20centered%20composition&width=400&height=300&seq=308&orientation=landscape',
      category: 'financeira',
      points: 9500,
      stock: 50,
      claimed: 18,
      available: true,
      featured: true
    }
  ];

  const cursos: Reward[] = [
    {
      id: 401,
      name: 'Curso Excel Avançado',
      description: 'Domine fórmulas, tabelas dinâmicas e macros em Excel',
      image: 'https://readdy.ai/api/search-image?query=excel%20spreadsheet%20training%20course%20on%20laptop%20screen%20with%20charts%20and%20formulas%2C%20professional%20educational%20content%2C%20high%20quality%2C%20centered%20composition&width=400&height=300&seq=401&orientation=landscape',
      category: 'curso',
      points: 800,
      stock: 100,
      claimed: 45,
      available: true,
      featured: true,
      expiresAt: '2024-12-31'
    },
    {
      id: 402,
      name: 'Inglês para Negócios',
      description: 'Curso completo de inglês focado em ambiente corporativo',
      image: 'https://readdy.ai/api/search-image?query=business%20english%20course%20with%20professional%20teacher%20and%20students%20in%20modern%20classroom%2C%20educational%20setting%2C%20high%20quality%2C%20centered%20composition&width=400&height=300&seq=402&orientation=landscape',
      category: 'curso',
      points: 1500,
      stock: 80,
      claimed: 38,
      available: true,
      featured: true,
      expiresAt: '2024-12-31'
    },
    {
      id: 403,
      name: 'Marketing Digital',
      description: 'Estratégias de marketing digital e redes sociais',
      image: 'https://readdy.ai/api/search-image?query=digital%20marketing%20course%20with%20social%20media%20icons%20and%20analytics%20dashboard%2C%20modern%20educational%20content%2C%20high%20quality%2C%20centered%20composition&width=400&height=300&seq=403&orientation=landscape',
      category: 'curso',
      points: 1200,
      stock: 90,
      claimed: 52,
      available: true,
      featured: true,
      expiresAt: '2024-12-31'
    },
    {
      id: 404,
      name: 'Gestão de Projetos',
      description: 'Metodologias ágeis e gestão eficiente de projetos',
      image: 'https://readdy.ai/api/search-image?query=project%20management%20course%20with%20kanban%20board%20and%20team%20collaboration%2C%20professional%20training%20environment%2C%20high%20quality%2C%20centered%20composition&width=400&height=300&seq=404&orientation=landscape',
      category: 'curso',
      points: 1000,
      stock: 70,
      claimed: 35,
      available: true,
      featured: false,
      expiresAt: '2024-12-31'
    },
    {
      id: 405,
      name: 'Power BI e Análise de Dados',
      description: 'Visualização de dados e business intelligence',
      image: 'https://readdy.ai/api/search-image?query=power%20BI%20data%20analysis%20course%20with%20interactive%20dashboards%20and%20charts%2C%20business%20intelligence%20training%2C%20high%20quality%2C%20centered%20composition&width=400&height=300&seq=405&orientation=landscape',
      category: 'curso',
      points: 900,
      stock: 85,
      claimed: 41,
      available: true,
      featured: false,
      expiresAt: '2024-12-31'
    },
    {
      id: 406,
      name: 'Liderança e Gestão de Equipes',
      description: 'Desenvolva habilidades de liderança e gestão',
      image: 'https://readdy.ai/api/search-image?query=leadership%20training%20course%20with%20team%20building%20activities%20and%20professional%20coach%2C%20corporate%20training%20environment%2C%20high%20quality%2C%20centered%20composition&width=400&height=300&seq=406&orientation=landscape',
      category: 'curso',
      points: 1300,
      stock: 60,
      claimed: 28,
      available: true,
      featured: true,
      expiresAt: '2024-12-31'
    },
    {
      id: 407,
      name: 'Programação Python',
      description: 'Aprenda Python do básico ao avançado',
      image: 'https://readdy.ai/api/search-image?query=python%20programming%20course%20with%20code%20on%20screen%20and%20developer%20workspace%2C%20tech%20education%20content%2C%20high%20quality%2C%20centered%20composition&width=400&height=300&seq=407&orientation=landscape',
      category: 'curso',
      points: 1400,
      stock: 75,
      claimed: 33,
      available: true,
      featured: false,
      expiresAt: '2024-12-31'
    },
    {
      id: 408,
      name: 'Oratória e Comunicação',
      description: 'Técnicas de apresentação e comunicação eficaz',
      image: 'https://readdy.ai/api/search-image?query=public%20speaking%20course%20with%20confident%20speaker%20presenting%20to%20audience%2C%20professional%20communication%20training%2C%20high%20quality%2C%20centered%20composition&width=400&height=300&seq=408&orientation=landscape',
      category: 'curso',
      points: 700,
      stock: 95,
      claimed: 48,
      available: true,
      featured: false
    },
    {
      id: 409,
      name: 'Design Thinking',
      description: 'Metodologia de inovação e resolução criativa de problemas',
      image: 'https://readdy.ai/api/search-image?query=design%20thinking%20workshop%20with%20sticky%20notes%20and%20creative%20brainstorming%20session%2C%20innovation%20training%20environment%2C%20high%20quality%2C%20centered%20composition&width=400&height=300&seq=409&orientation=landscape',
      category: 'curso',
      points: 1100,
      stock: 65,
      claimed: 30,
      available: true,
      featured: true,
      expiresAt: '2024-12-31'
    },
    {
      id: 410,
      name: 'Finanças Pessoais',
      description: 'Planejamento financeiro e investimentos',
      image: 'https://readdy.ai/api/search-image?query=personal%20finance%20course%20with%20investment%20charts%20and%20money%20management%20concepts%2C%20financial%20education%20content%2C%20high%20quality%2C%20centered%20composition&width=400&height=300&seq=410&orientation=landscape',
      category: 'curso',
      points: 600,
      stock: 110,
      claimed: 55,
      available: true,
      featured: false,
      expiresAt: '2024-12-31'
    },
    {
      id: 411,
      name: 'Photoshop e Design Gráfico',
      description: 'Edição de imagens e criação de designs profissionais',
      image: 'https://readdy.ai/api/search-image?query=photoshop%20graphic%20design%20course%20with%20creative%20workspace%20and%20design%20tools%2C%20professional%20design%20training%2C%20high%20quality%2C%20centered%20composition&width=400&height=300&seq=411&orientation=landscape',
      category: 'curso',
      points: 950,
      stock: 70,
      claimed: 37,
      available: true,
      featured: false,
      expiresAt: '2024-12-31'
    },
    {
      id: 412,
      name: 'Vendas e Negociação',
      description: 'Técnicas avançadas de vendas e negociação',
      image: 'https://readdy.ai/api/search-image?query=sales%20negotiation%20training%20course%20with%20business%20handshake%20and%20professional%20meeting%2C%20corporate%20sales%20training%2C%20high%20quality%2C%20centered%20composition&width=400&height=300&seq=412&orientation=landscape',
      category: 'curso',
      points: 850,
      stock: 80,
      claimed: 42,
      available: true,
      featured: true,
      expiresAt: '2024-12-31'
    }
  ];

  const claimedRewards: ClaimedReward[] = [
    {
      id: 1,
      rewardId: 1,
      rewardName: 'Smartphone Samsung Galaxy A54',
      rewardImage: 'https://readdy.ai/api/search-image?query=modern%20smartphone%20samsung%20galaxy%20on%20clean%20white%20background%2C%20professional%20product%20photography%2C%20high%20quality%2C%20centered%20composition&width=400&height=300&seq=1&orientation=landscape',
      points: 2500,
      claimedDate: '2024-01-15',
      status: 'entregue',
      trackingCode: 'BR123456789'
    },
    {
      id: 2,
      rewardId: 102,
      rewardName: 'Vale-Presente iFood R$ 100',
      rewardImage: 'https://readdy.ai/api/search-image?query=ifood%20gift%20card%20voucher%20on%20clean%20white%20background%20with%20ifood%20logo%2C%20professional%20product%20photography%2C%20high%20quality%2C%20centered%20composition&width=400&height=300&seq=102&orientation=landscape',
      points: 900,
      claimedDate: '2024-01-20',
      status: 'processando'
    },
    {
      id: 3,
      rewardId: 201,
      rewardName: 'Spa Day Completo',
      rewardImage: 'https://readdy.ai/api/search-image?query=luxury%20spa%20day%20experience%20with%20massage%20stones%20and%20candles%2C%20serene%20atmosphere%2C%20professional%20photography%2C%20high%20quality%2C%20centered%20composition&width=400&height=300&seq=201&orientation=landscape',
      points: 2500,
      claimedDate: '2024-01-25',
      status: 'enviado',
      trackingCode: 'SPA987654321'
    },
    {
      id: 4,
      rewardId: 301,
      rewardName: 'Cashback R$ 100',
      rewardImage: 'https://readdy.ai/api/search-image?query=cashback%20money%20return%20concept%20with%20brazilian%20real%20bills%20and%20coins%2C%20financial%20reward%2C%20professional%20photography%2C%20high%20quality%2C%20centered%20composition&width=400&height=300&seq=301&orientation=landscape',
      points: 1000,
      claimedDate: '2024-02-01',
      status: 'entregue'
    },
    {
      id: 5,
      rewardId: 3,
      rewardName: 'Cafeteira Nespresso',
      rewardImage: 'https://readdy.ai/api/search-image?query=nespresso%20coffee%20machine%20on%20clean%20white%20background%2C%20professional%20product%20photography%2C%20high%20quality%2C%20centered%20composition&width=400&height=300&seq=3&orientation=landscape',
      points: 1200,
      claimedDate: '2024-02-05',
      status: 'pendente'
    }
  ];

  // Get current items based on active tab and pagination
  const getCurrentItems = () => {
    let items: Reward[] = [];
    
    switch (activeTab) {
      case 'produtos':
        items = selectedCategory === 'todos' 
          ? rewards 
          : rewards.filter(reward => reward.category === selectedCategory);
        break;
      case 'vouchers':
        items = vouchers;
        break;
      case 'experiencias':
        items = experiencias;
        break;
      case 'financeiras':
        items = financeiras;
        break;
      case 'cursos':
        items = cursos;
        break;
      default:
        items = [];
    }

    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return items.slice(startIndex, endIndex);
  };

  // Get total items count
  const getTotalItems = () => {
    switch (activeTab) {
      case 'produtos':
        return selectedCategory === 'todos' 
          ? rewards.length 
          : rewards.filter(reward => reward.category === selectedCategory).length;
      case 'vouchers':
        return vouchers.length;
      case 'experiencias':
        return experiencias.length;
      case 'financeiras':
        return financeiras.length;
      case 'cursos':
        return cursos.length;
      default:
        return 0;
    }
  };

  // Calculate total pages
  const totalPages = Math.ceil(getTotalItems() / itemsPerPage);

  // Reset pagination when changing tabs or categories
  const handleTabChange = (tab: 'produtos' | 'vouchers' | 'experiencias' | 'financeiras' | 'cursos') => {
    setActiveTab(tab);
    setCurrentPage(1);
    setSelectedCards(new Set());
  };

  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category);
    setCurrentPage(1);
    setSelectedCards(new Set());
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  // Card selection handlers
  const handleCardSelection = (cardId: number) => {
    const newSelected = new Set(selectedCards);
    if (newSelected.has(cardId)) {
      newSelected.delete(cardId);
    } else {
      newSelected.add(cardId);
    }
    setSelectedCards(newSelected);
  };

  const handleSelectAll = () => {
    const currentItems = getCurrentItems();
    const allCurrentIds = currentItems.map(item => item.id);
    
    // Check if all current items are selected
    const allSelected = allCurrentIds.every(id => selectedCards.has(id));
    
    if (allSelected) {
      // Deselect all current items
      const newSelected = new Set(selectedCards);
      allCurrentIds.forEach(id => newSelected.delete(id));
      setSelectedCards(newSelected);
    } else {
      // Select all current items
      const newSelected = new Set(selectedCards);
      allCurrentIds.forEach(id => newSelected.add(id));
      setSelectedCards(newSelected);
    }
  };

  const handleVincularCampanha = () => {
    if (!selectedCampaign || selectedCards.size === 0) {
      alert('Selecione uma campanha e pelo menos uma recompensa!');
      return;
    }
    
    setShowVinculationModal(true);
  };

  const handleConfirmVinculation = () => {
    const campaign = campaigns.find(c => c.id === selectedCampaign);
    let vinculationMessage = '';
    
    switch (vinculationType) {
      case 'meta':
        const meta = campaign?.metas.find(m => m.id === selectedMeta);
        vinculationMessage = `Recompensas vinculadas à meta "${meta?.nome}" da campanha "${campaign?.name}" com taxa administrativa de ${taxaAdministrativa}%`;
        break;
      case 'gamificacao':
        const gamificacao = campaign?.gamificacoes.find(g => g.id === selectedGamificacao);
        vinculationMessage = `Recompensas vinculadas à gamificação "${gamificacao?.nome}" da campanha "${campaign?.name}" com taxa administrativa de ${taxaAdministrativa}%`;
        break;
      case 'produto':
        vinculationMessage = `Recompensas vinculadas como produtos da campanha "${campaign?.name}"`;
        break;
    }
    
    console.log(vinculationMessage); // Log para debug
    
    setShowVinculationModal(false);
    setShowVinculationSuccess(true);
    
    // Clear selections after vinculation
    setTimeout(() => {
      setSelectedCards(new Set());
      setShowVinculationSuccess(false);
      setSelectedCampaign(null);
      setSelectedMeta(null);
      setSelectedGamificacao(null);
      setVinculationType('produto');
      setTaxaAdministrativa(5.0);
    }, 3000);
  };

  // Check if all current items are selected
  const areAllCurrentSelected = () => {
    const currentItems = getCurrentItems();
    if (currentItems.length === 0) return false;
    return currentItems.every(item => selectedCards.has(item.id));
  };

  // Pagination component
  const PaginationControls = () => {
    if (activeTab === 'historico') return null;

    const totalItems = getTotalItems();
    const startItem = (currentPage - 1) * itemsPerPage + 1;
    const endItem = Math.min(currentPage * itemsPerPage, totalItems);

    return (
      <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mt-8 p-4 bg-gray-50 rounded-lg">
        {/* Items per page selector */}
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">Itens por página:</span>
          <select
            value={itemsPerPage}
            onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
            className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 pr-8"
          >
            <option value={6}>6</option>
            <option value={12}>12</option>
            <option value={24}>24</option>
            <option value={48}>48</option>
          </select>
        </div>

        {/* Items info */}
        <div className="text-sm text-gray-600">
          Mostrando {startItem} a {endItem} de {totalItems} itens
        </div>

        {/* Pagination buttons */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className="px-3 py-1 border border-gray-300 rounded-md text-sm hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
          >
            <i className="ri-arrow-left-line"></i>
            Anterior
          </button>

          {/* Page numbers */}
          <div className="flex items-center gap-1">
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }

              return (
                <button
                  key={pageNum}
                  onClick={() => setCurrentPage(pageNum)}
                  className={`px-3 py-1 text-sm rounded-md ${
                    currentPage === pageNum
                      ? 'bg-blue-500 text-white'
                      : 'border border-gray-300 hover:bg-gray-100'
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
          </div>

          <button
            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
            className="px-3 py-1 border border-gray-300 rounded-md text-sm hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
          >
            Próxima
            <i className="ri-arrow-right-line"></i>
          </button>
        </div>
      </div>
    );
  };

  const handleClaimReward = (reward: Reward) => {
    if (userPoints < reward.points) {
      alert('Pontos insuficientes para resgatar esta recompensa!');
      return;
    }
    setSelectedReward(reward);
    setShowConfirmModal(true);
  };

  const confirmClaim = () => {
    setShowConfirmModal(false);
    setShowSuccessModal(true);
    setSelectedReward(null);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pendente': return 'bg-yellow-100 text-yellow-800';
      case 'processando': return 'bg-blue-100 text-blue-800';
      case 'enviado': return 'bg-purple-100 text-purple-800';
      case 'entregue': return 'bg-green-100 text-green-800';
      case 'cancelado': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pendente': return 'Pendente';
      case 'processando': return 'Processando';
      case 'enviado': return 'Enviado';
      case 'entregue': return 'Entregue';
      case 'cancelado': return 'Cancelado';
      default: return status;
    }
  };

  // Calcula quantidade de resgatados conforme a campanha selecionada
  const getClaimedPorCampanha = (itemId: number, baseClaimed: number): number => {
    if (!selectedCampaign) return baseClaimed;
    return ((selectedCampaign * 47 + itemId * 23) % Math.max(baseClaimed * 2, 20)) + Math.floor(baseClaimed * 0.4);
  };

  // Render reward card
  const renderRewardCard = (reward: Reward, colorScheme: string) => {
    const isSelected = selectedCards.has(reward.id);
    
    return (
      <div 
        key={reward.id} 
        className={`bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-all h-full flex flex-col relative ${
          isSelected ? 'ring-2 ring-blue-500' : ''
        }`}
      >
        {/* Selection Checkbox */}
        <div className="absolute top-2 left-2 z-10">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => handleCardSelection(reward.id)}
            className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
          />
        </div>

        <div className="relative">
          <img
            src={reward.image}
            alt={reward.name}
            className="w-full h-48 object-cover object-top"
          />
          {/* XP Badge on Image */}
          <div className="absolute top-2 right-2 bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-3 py-1.5 rounded-full text-sm font-bold shadow-lg flex items-center gap-1.5">
            <i className="ri-star-fill text-base"></i>
            <span>{reward.points.toLocaleString()} XP</span>
          </div>
          {reward.featured && (
            <div className={`absolute bottom-2 left-2 ${colorScheme} px-2 py-1 rounded-full text-xs font-medium`}>
              Destaque
            </div>
          )}
          <div className="absolute bottom-2 right-4 bg-white bg-opacity-90 px-2 py-1 rounded-full text-xs font-medium">
            {reward.stock} disponíveis
          </div>
        </div>
        
        <div className="p-4 flex-1 flex flex-col">
          <h3 className="font-semibold text-gray-900 mb-2 min-h-[3rem]">{reward.name}</h3>
          <p className="text-gray-600 text-sm mb-3 line-clamp-2 min-h-[2.5rem]">{reward.description}</p>
          {reward.expiresAt && (
            <p className="text-xs text-orange-600 mb-2">
              <i className="ri-time-line mr-1"></i>
              Válido até {new Date(reward.expiresAt).toLocaleDateString('pt-BR')}
            </p>
          )}
          <div className="flex justify-between items-center mb-3">
            <span className={`text-lg font-bold ${colorScheme.includes('yellow') ? 'text-yellow-600' : colorScheme.includes('green') ? 'text-green-600' : colorScheme.includes('purple') ? 'text-purple-600' : colorScheme.includes('emerald') ? 'text-emerald-600' : colorScheme.includes('orange') ? 'text-orange-600' : 'text-blue-600'}`}>
              {reward.points.toLocaleString()} pts
            </span>
            <span className="text-sm text-gray-500">
              {selectedCampaign ? `${getClaimedPorCampanha(reward.id, reward.claimed)} resgatados` : ''}
            </span>
          </div>
        </div>
      </div>
    );
  };

  // Cart functions
  const addToCart = (voucher: Reward) => {
    const existingItem = cart.find(item => item.id === voucher.id);
    if (existingItem) {
      setCart(cart.map(item => 
        item.id === voucher.id 
          ? { ...item, quantity: item.quantity + 1 }
          : item
      ));
    } else {
      setCart([...cart, {
        id: voucher.id,
        name: voucher.name,
        price: voucher.price || 0,
        quantity: 1,
        image: voucher.image,
        adminFee: voucher.adminFee || 0
      }]);
    }
  };

  const removeFromCart = (itemId: number) => {
    setCart(cart.filter(item => item.id !== itemId));
  };

  const updateQuantity = (itemId: number, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(itemId);
    } else {
      setCart(cart.map(item => 
        item.id === itemId 
          ? { ...item, quantity }
          : item
      ));
    }
  };

  const getCartTotal = () => {
    return cart.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const getCartAdminFeeTotal = () => {
    return cart.reduce((total, item) => total + ((item.price * item.adminFee / 100) * item.quantity), 0);
  };

  const getCartGrandTotal = () => {
    return getCartTotal() + getCartAdminFeeTotal();
  };

  const getCartItemCount = () => {
    return cart.reduce((total, item) => total + item.quantity, 0);
  };

  const handleCheckout = () => {
    setShowCart(false);
    setShowCheckout(true);
  };

  const confirmPurchase = () => {
    if (paymentMethod === 'pix') {
      setAwaitingPayment(true);
    } else {
      // Para boleto, confirma imediatamente
      setCart([]);
      setShowCheckout(false);
      setShowSuccessModal(true);
    }
  };

  // Render voucher card with cart functionality
  const renderVoucherCard = (voucher: Reward) => {
    const isSelected = selectedCards.has(voucher.id);
    const adminFeeValue = Math.round((voucher.price || 0) * (voucher.adminFee || 0) / 100);
    const totalPrice = (voucher.price || 0) + adminFeeValue;
    
    return (
      <div 
        key={voucher.id} 
        className={`bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-all h-full flex flex-col relative ${
          isSelected ? 'ring-2 ring-blue-500' : ''
        }`}
      >
        {/* Selection Checkbox */}
        <div className="absolute top-2 left-2 z-10">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => handleCardSelection(voucher.id)}
            className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
          />
        </div>

        <div className="relative">
          <img
            src={voucher.image}
            alt={voucher.name}
            className="w-full h-48 object-cover object-top"
          />
          {/* XP Badge on Image */}
          <div className="absolute top-2 right-2 bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-3 py-1.5 rounded-full text-sm font-bold shadow-lg flex items-center gap-1.5">
            <i className="ri-star-fill text-base"></i>
            <span>{totalPrice.toLocaleString()} XP</span>
          </div>
          {voucher.featured && (
            <div className="absolute bottom-2 left-2 bg-green-400 text-green-900 px-2 py-1 rounded-full text-xs font-medium">
              Destaque
            </div>
          )}
          <div className="absolute bottom-2 right-4 bg-white bg-opacity-90 px-2 py-1 rounded-full text-xs font-medium">
            {voucher.stock} disponíveis
          </div>
        </div>
        
        <div className="p-4 flex-1 flex flex-col">
          <h3 className="font-semibold text-gray-900 mb-2 min-h-[3rem]">{voucher.name}</h3>
          <p className="text-gray-600 text-sm mb-3 line-clamp-2 min-h-[2.5rem]">{voucher.description}</p>
          {voucher.expiresAt && (
            <p className="text-xs text-orange-600 mb-2">
              <i className="ri-time-line mr-1"></i>
              Válido até {new Date(voucher.expiresAt).toLocaleDateString('pt-BR')}
            </p>
          )}
          
          {/* Pricing with Admin Fee */}
          <div className="mb-3">
            <div className="flex justify-between items-center mb-1">
              <span className="text-sm text-gray-600">Valor do voucher:</span>
              <span className="text-sm font-medium text-gray-900">
                {voucher.price?.toLocaleString()} pts
              </span>
            </div>
            <div className="flex justify-between items-center mb-1">
              <span className="text-sm text-gray-600">Taxa admin ({voucher.adminFee}%):</span>
              <span className="text-sm font-medium text-orange-600">
                {adminFeeValue.toLocaleString()} pts
              </span>
            </div>
            <div className="border-t border-gray-200 pt-1">
              <div className="flex justify-between">
                <span className="text-lg font-bold text-gray-900">Total:</span>
                <span className="text-xl font-bold text-green-600">
                  {totalPrice.toLocaleString()} pts
                </span>
              </div>
            </div>
            <div className="mt-1 flex justify-between items-center">
              <span className="text-sm text-gray-500">
                {selectedCampaign ? `${getClaimedPorCampanha(voucher.id, voucher.claimed)} resgatados` : ''}
              </span>
              <span className="text-sm text-gray-500">
                {selectedCampaign ? `${getClaimedPorCampanha(voucher.id, voucher.claimed)} vendidos` : ''}
              </span>
            </div>
          </div>
          
          {/* Add to Cart Button */}
          <button
            onClick={() => addToCart(voucher)}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg transition-colors flex items-center justify-center gap-2 mt-auto"
          >
            <i className="ri-shopping-cart-line"></i>
            Adicionar ao Carrinho
          </button>
        </div>
      </div>
    );
  };

  return (
    <Layout>
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Recompensas</h1>
            <p className="text-gray-600">Resgate suas recompensas com os pontos acumulados</p>
          </div>
          <div className="flex items-center gap-3">
            {/* Cart and Report Icons - Only show for vouchers tab */}
            {activeTab === 'vouchers' && (
              <>
                <button
                  onClick={() => setShowCart(true)}
                  className="relative p-3 bg-green-50 hover:bg-green-100 text-green-600 rounded-lg transition-colors cursor-pointer"
                  title="Carrinho de Compras"
                >
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-shopping-cart-line text-xl"></i>
                  </div>
                  {getCartItemCount() > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                      {getCartItemCount()}
                    </span>
                  )}
                </button>
                <button
                  onClick={() => setShowPurchaseReport(true)}
                  className="p-3 bg-purple-50 hover:bg-purple-100 text-purple-600 rounded-lg transition-colors cursor-pointer"
                  title="Relatório de Compras"
                >
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-file-list-3-line text-xl"></i>
                  </div>
                </button>
              </>
            )}
          </div>
        </div>

        {/* Seletor de Campanha */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex items-center gap-4">
            <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
              Selecionar a campanha:
            </label>
            <select
              value={selectedCampaign || ''}
              onChange={(e) => setSelectedCampaign(Number(e.target.value) || null)}
              className="flex-1 max-w-md px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Selecione uma campanha</option>
              {campaigns.map((campanha) => (
                <option key={campanha.id} value={campanha.id}>
                  {campanha.name}
                </option>
              ))}
            </select>
            {selectedCards.size > 0 && selectedCampaign && (
              <button
                onClick={handleVincularCampanha}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-link"></i>
                </div>
                Vincular Selecionados ({selectedCards.size})
              </button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8 overflow-x-auto">
            {[
              { id: 'produtos', label: 'Produtos', icon: 'ri-store-line' },
              { id: 'vouchers', label: 'Vouchers', icon: 'ri-gift-line' },
              { id: 'experiencias', label: 'Experiências', icon: 'ri-compass-line' },
              { id: 'financeiras', label: 'Financeiras', icon: 'ri-money-dollar-circle-line' },
              { id: 'cursos', label: 'Cursos', icon: 'ri-book-line' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id as any)}
                className={`flex items-center gap-2 py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <i className={`${tab.icon} text-lg`}></i>
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Products Tab */}
        {activeTab === 'produtos' && (
          <div>
            {/* Category Filter and Select All */}
            <div className="mb-6 space-y-4">
              <div className="flex flex-wrap gap-2">
                {[
                  { id: 'todos', label: 'Todos os Produtos' },
                  { id: 'eletronicos', label: 'Eletrônicos' },
                  { id: 'casa', label: 'Casa e Decoração' },
                  { id: 'esporte', label: 'Esporte e Lazer' },
                  { id: 'moda', label: 'Moda e Acessórios' },
                  { id: 'beleza', label: 'Beleza e Cuidados' }
                ].map((category) => (
                  <button
                    key={category.id}
                    onClick={() => handleCategoryChange(category.id)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                      selectedCategory === category.id
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {category.label}
                  </button>
                ))}
              </div>

              {/* Select All Button for Products */}
              <div className="flex items-center justify-between">
                <button
                  onClick={handleSelectAll}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                >
                  <input
                    type="checkbox"
                    checked={areAllCurrentSelected()}
                    onChange={() => {}}
                    className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                  />
                  <span className="text-sm font-medium">
                    {areAllCurrentSelected() ? 'Desmarcar Todos' : 'Selecionar Todos'}
                  </span>
                  <span className="text-xs text-blue-600">
                    ({getCurrentItems().length} itens na página)
                  </span>
                </button>
                {selectedCards.size > 0 && (
                  <span className="text-sm text-gray-600">
                    {selectedCards.size} item(s) selecionado(s)
                  </span>
                )}
              </div>
            </div>

            {/* Products Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {getCurrentItems().map((reward) => renderRewardCard(reward, 'bg-yellow-400 text-yellow-900'))}
            </div>

            <PaginationControls />
          </div>
        )}

        {/* Vouchers Tab */}
        {activeTab === 'vouchers' && (
          <div>
            {/* Category Filter and Select All */}
            <div className="mb-6">
              <div className="flex flex-wrap gap-2">
                {[
                  { id: 'todos', label: 'Todos os Vouchers' },
                  { id: 'alimentacao', label: 'Alimentação' },
                  { id: 'vestuario', label: 'Vestuário' },
                  { id: 'entretenimento', label: 'Entretenimento' },
                  { id: 'brinquedos', label: 'Brinquedos' },
                  { id: 'transporte', label: 'Transporte' },
                  { id: 'tecnologia', label: 'Tecnologia' },
                  { id: 'varejo', label: 'Varejo' },
                  { id: 'games', label: 'Games' },
                  { id: 'beleza', label: 'Beleza' },
                  { id: 'esportes', label: 'Esportes' },
                  { id: 'casa', label: 'Casa' },
                  { id: 'livros', label: 'Livros' }
                ].map((category) => (
                  <button
                    key={category.id}
                    onClick={() => handleCategoryChange(category.id)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                      selectedCategory === category.id
                        ? 'bg-green-500 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {category.label}
                  </button>
                ))}
              </div>

              {/* Select All Button for Vouchers */}
              <div className="mb-6">
                <div className="flex items-center justify-between">
                  <button
                    onClick={handleSelectAll}
                    className="flex items-center gap-2 px-4 py-2 bg-green-50 hover:bg-green-100 text-green-700 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <input
                      type="checkbox"
                      checked={areAllCurrentSelected()}
                      onChange={() => {}}
                      className="w-4 h-4 rounded border-gray-300 text-green-600 focus:ring-green-500 cursor-pointer"
                    />
                    <span className="text-sm font-medium">
                      {areAllCurrentSelected() ? 'Desmarcar Todos' : 'Selecionar Todos'}
                    </span>
                    <span className="text-xs text-green-600">
                      ({getCurrentItems().length} itens na página)
                    </span>
                  </button>
                  {selectedCards.size > 0 && (
                    <span className="text-sm text-gray-600">
                      {selectedCards.size} item(s) selecionado(s)
                    </span>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {getCurrentItems().map((voucher) => renderVoucherCard(voucher))}
              </div>

              <PaginationControls />
            </div>
          </div>
        )}

        {/* Experiências Tab */}
        {activeTab === 'experiencias' && (
          <div>
            {/* Category Filter and Select All */}
            <div className="mb-6">
              <div className="flex flex-wrap gap-2">
                {[
                  { id: 'todos', label: 'Todos as Experiências' },
                  { id: 'spa', label: 'Spa' },
                  { id: 'jantar', label: 'Jantar' },
                  { id: 'culinaria', label: 'Culinária' },
                  { id: 'balao', label: 'Passeio de Balão' },
                  { id: 'resort', label: 'Day Use Resort' },
                  { id: 'vinhos', label: 'Experiência de Vinhos' },
                  { id: 'rafting', label: 'Aventura de Rafting' },
                  { id: 'show', label: 'Show Musical VIP' }
                ].map((category) => (
                  <button
                    key={category.id}
                    onClick={() => handleCategoryChange(category.id)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                      selectedCategory === category.id
                        ? 'bg-purple-500 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {category.label}
                  </button>
                ))}
              </div>

              {/* Select All Button for Experiências */}
              <div className="mb-6">
                <div className="flex items-center justify-between">
                  <button
                    onClick={handleSelectAll}
                    className="flex items-center gap-2 px-4 py-2 bg-purple-50 hover:bg-purple-100 text-purple-700 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <input
                      type="checkbox"
                      checked={areAllCurrentSelected()}
                      onChange={() => {}}
                      className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500 cursor-pointer"
                    />
                    <span className="text-sm font-medium">
                      {areAllCurrentSelected() ? 'Desmarcar Todos' : 'Selecionar Todos'}
                    </span>
                    <span className="text-xs text-purple-600">
                      ({getCurrentItems().length} itens na página)
                    </span>
                  </button>
                  {selectedCards.size > 0 && (
                    <span className="text-sm text-gray-600">
                      {selectedCards.size} item(s) selecionado(s)
                    </span>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {getCurrentItems().map((experiencia) => renderRewardCard(experiencia, 'bg-purple-400 text-purple-900'))}
              </div>

              <PaginationControls />
            </div>
          </div>
        )}

        {/* Financeiras Tab */}
        {activeTab === 'financeiras' && (
          <div className="space-y-6">
            {/* Aviso sobre prêmios financeiros */}
            <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
              <span className="text-lg leading-none mt-0.5 flex-shrink-0">💡</span>
              <p className="text-sm text-amber-800">
                <strong>Atenção:</strong> Selecione os tipos de prêmios financeiros que deseja vincular à campanha selecionada. Use o checkbox no canto superior esquerdo de cada card e clique em "Vincular Selecionados".
              </p>
            </div>

            {/* Select All e contagem para Financeiras */}
            <div className="flex items-center justify-between">
              <button
                onClick={() => {
                  const financialIds = [10001, 10002, 10003];
                  const allSelected = financialIds.every(id => selectedCards.has(id));
                  const newSelected = new Set(selectedCards);
                  if (allSelected) {
                    financialIds.forEach(id => newSelected.delete(id));
                  } else {
                    financialIds.forEach(id => newSelected.add(id));
                  }
                  setSelectedCards(newSelected);
                }}
                className="flex items-center gap-2 px-4 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                <input
                  type="checkbox"
                  checked={[10001, 10002, 10003].every(id => selectedCards.has(id))}
                  onChange={() => {}}
                  className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                />
                <span className="text-sm font-medium">
                  {[10001, 10002, 10003].every(id => selectedCards.has(id)) ? 'Desmarcar Todos' : 'Selecionar Todos'}
                </span>
                <span className="text-xs text-blue-600">
                  (3 itens)
                </span>
              </button>
              {selectedCards.size > 0 && (
                <span className="text-sm text-gray-600">
                  {selectedCards.size} item(s) selecionado(s)
                </span>
              )}
            </div>

            {/* Cards financeiros — mesmo layout do /premiado/recompensas, agora com seleção global */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <AdminPixPagamentoCard
                id={10001}
                modalidade="pix"
                selectedCampaignId={selectedCampaign}
                isSelected={selectedCards.has(10001)}
                onSelect={() => handleCardSelection(10001)}
              />
              <AdminPixPagamentoCard
                id={10003}
                modalidade="pagamento"
                selectedCampaignId={selectedCampaign}
                isSelected={selectedCards.has(10003)}
                onSelect={() => handleCardSelection(10003)}
              />
              <AdminFinancialCard
                id={10002}
                tipo="cashback"
                imagem="https://static.readdy.ai/image/22e92c18f0b031d861a5e53bf8e362fb/9efecf4d720f800189f4073a3af54c3a.png"
                selectedCampaignId={selectedCampaign}
                isSelected={selectedCards.has(10002)}
                onSelect={() => handleCardSelection(10002)}
              />
            </div>
          </div>
        )}

        {/* Cursos Tab */}
        {activeTab === 'cursos' && (
          <div>
            {/* Category Filter and Select All */}
            <div className="mb-6">
              <div className="flex flex-wrap gap-2">
                {[
                  { id: 'todos', label: 'Todos os Cursos' },
                  { id: 'excel', label: 'Excel' },
                  { id: 'ingles', label: 'Inglês' },
                  { id: 'marketing', label: 'Marketing Digital' },
                  { id: 'projeto', label: 'Gestão de Projetos' },
                  { id: 'powerbi', label: 'Power BI' },
                  { id: 'lideranca', label: 'Liderança' },
                  { id: 'python', label: 'Programação Python' },
                  { id: 'comunicação', label: 'Oratória e Comunicação' },
                  { id: 'design', label: 'Design Thinking' },
                  { id: 'financas', label: 'Finanças Pessoais' },
                  { id: 'photoshop', label: 'Photoshop' },
                  { id: 'vendas', label: 'Vendas e Negociação' }
                ].map((category) => (
                  <button
                    key={category.id}
                    onClick={() => handleCategoryChange(category.id)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                      selectedCategory === category.id
                        ? 'bg-orange-500 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {category.label}
                  </button>
                ))}
              </div>

              {/* Select All Button for Cursos */}
              <div className="mb-6">
                <div className="flex items-center justify-between">
                  <button
                    onClick={handleSelectAll}
                    className="flex items-center gap-2 px-4 py-2 bg-orange-50 hover:bg-orange-100 text-orange-700 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <input
                      type="checkbox"
                      checked={areAllCurrentSelected()}
                      onChange={() => {}}
                      className="w-4 h-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500 cursor-pointer"
                    />
                    <span className="text-sm font-medium">
                      {areAllCurrentSelected() ? 'Desmarcar Todos' : 'Selecionar Todos'}
                    </span>
                    <span className="text-xs text-orange-600">
                      ({getCurrentItems().length} itens na página)
                    </span>
                  </button>
                  {selectedCards.size > 0 && (
                    <span className="text-sm text-gray-600">
                      {selectedCards.size} item(s) selecionado(s)
                    </span>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {getCurrentItems().map((curso) => renderRewardCard(curso, 'bg-orange-400 text-orange-900'))}
              </div>

              <PaginationControls />
            </div>
          </div>
        )}

        {/* Modals continuam iguais... */}
      </div>

      {/* Modal de Confirmação de Resgate */}
      {showConfirmModal && selectedReward && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Confirmar Resgate</h3>
            <div className="mb-4">
              <img
                src={selectedReward.image}
                alt={selectedReward.name}
                className="w-full h-48 object-cover object-top rounded-lg mb-4"
              />
              <h4 className="font-semibold text-gray-900 mb-2">{selectedReward.name}</h4>
              <p className="text-gray-600 text-sm mb-4">{selectedReward.description}</p>
              <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                <span className="text-gray-700">Pontos necessários:</span>
                <span className="text-xl font-bold text-blue-600">{selectedReward.points.toLocaleString()} pts</span>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowConfirmModal(false);
                  setSelectedReward(null);
                }}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={confirmClaim}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Confirmar Resgate
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Sucesso */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-check-line text-3xl text-green-600"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Resgate Realizado!</h3>
            <p className="text-gray-600 mb-6">
              Sua recompensa foi resgatada com sucesso. Você receberá mais informações por e-mail.
            </p>
            <button
              onClick={() => setShowSuccessModal(false)}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Fechar
            </button>
          </div>
        </div>
      )}

      {/* Modal de Vinculação */}
      {showVinculationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Vincular Recompensas à Campanha</h3>
            
            <div className="mb-6">
              <p className="text-gray-600 mb-2">
                Campanha selecionada: <strong>{campaigns.find(c => c.id === selectedCampaign)?.name}</strong>
              </p>
              <p className="text-gray-600">
                Recompensas selecionadas: <strong>{selectedCards.size}</strong>
              </p>
            </div>

            {/* Tipo de Vinculação */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tipo de Vinculação
              </label>
              <div className="space-y-2">
                <label className="flex items-center p-3 border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer">
                  <input
                    type="radio"
                    name="vinculationType"
                    value="produto"
                    checked={vinculationType === 'produto'}
                    onChange={(e) => setVinculationType(e.target.value as any)}
                    className="mr-3"
                  />
                  <div>
                    <div className="font-medium text-gray-900">Produto da Campanha</div>
                    <div className="text-sm text-gray-600">Vincular como produto disponível na campanha</div>
                  </div>
                </label>
                <label className="flex items-center p-3 border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer">
                  <input
                    type="radio"
                    name="vinculationType"
                    value="meta"
                    checked={vinculationType === 'meta'}
                    onChange={(e) => setVinculationType(e.target.value as any)}
                    className="mr-3"
                  />
                  <div>
                    <div className="font-medium text-gray-900">Meta Específica</div>
                    <div className="text-sm text-gray-600">Vincular como recompensa de uma meta</div>
                  </div>
                </label>
                <label className="flex items-center p-3 border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer">
                  <input
                    type="radio"
                    name="vinculationType"
                    value="gamificacao"
                    checked={vinculationType === 'gamificacao'}
                    onChange={(e) => setVinculationType(e.target.value as any)}
                    className="mr-3"
                  />
                  <div>
                    <div className="font-medium text-gray-900">Gamificação</div>
                    <div className="text-sm text-gray-600">Vincular como recompensa de gamificação</div>
                  </div>
                </label>
              </div>
            </div>

            {/* Seleção de Meta */}
            {vinculationType === 'meta' && (
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Selecionar Meta
                </label>
                <select
                  value={selectedMeta || ''}
                  onChange={(e) => setSelectedMeta(Number(e.target.value) || null)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Selecione uma meta</option>
                  {campaigns.find(c => c.id === selectedCampaign)?.metas.map((meta) => (
                    <option key={meta.id} value={meta.id}>
                      {meta.nome} - {meta.descricao}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Seleção de Gamificação */}
            {vinculationType === 'gamificacao' && (
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Selecionar Gamificação
                </label>
                <select
                  value={selectedGamificacao || ''}
                  onChange={(e) => setSelectedGamificacao(Number(e.target.value) || null)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Selecione uma gamificação</option>
                  {campaigns.find(c => c.id === selectedCampaign)?.gamificacoes.map((gamificacao) => (
                    <option key={gamificacao.id} value={gamificacao.id}>
                      {gamificacao.nome} - {gamificacao.descricao}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Taxa Administrativa */}
            {(vinculationType === 'meta' || vinculationType === 'gamificacao') && (
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Taxa Administrativa (%)
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={taxaAdministrativa}
                  onChange={(e) => setTaxaAdministrativa(Number(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowVinculationModal(false);
                  setVinculationType('produto');
                  setSelectedMeta(null);
                  setSelectedGamificacao(null);
                  setTaxaAdministrativa(5.0);
                }}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirmVinculation}
                disabled={
                  (vinculationType === 'meta' && !selectedMeta) ||
                  (vinculationType === 'gamificacao' && !selectedGamificacao)
                }
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Confirmar Vinculação
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Sucesso de Vinculação */}
      {showVinculationSuccess && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-check-line text-3xl text-green-600"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Vinculação Realizada!</h3>
            <p className="text-gray-600 mb-6">
              As recompensas foram vinculadas à campanha com sucesso.
            </p>
            <button
              onClick={() => setShowVinculationSuccess(false)}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Fechar
            </button>
          </div>
        </div>
      )}

      {/* Modal do Carrinho de Compras */}
      {showCart && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-900">Carrinho de Compras</h3>
                <button
                  onClick={() => setShowCart(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {cart.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-shopping-cart-line text-3xl text-gray-400"></i>
                  </div>
                  <p className="text-gray-600">Seu carrinho está vazio</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {cart.map((item) => (
                    <div key={item.id} className="flex gap-4 p-4 border border-gray-200 rounded-lg">
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-24 h-24 object-cover object-top rounded-lg"
                      />
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900 mb-2">{item.name}</h4>
                        <div className="space-y-1 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Valor unitário:</span>
                            <span className="font-medium">{item.price.toLocaleString()} pts</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Taxa admin ({item.adminFee}%):</span>
                            <span className="font-medium text-orange-600">
                              {Math.round(item.price * item.adminFee / 100).toLocaleString()} pts
                            </span>
                          </div>
                          <div className="flex justify-between border-t border-gray-200 pt-1">
                            <span className="text-gray-900 font-medium">Total unitário:</span>
                            <span className="font-bold text-green-600">
                              {(item.price + Math.round(item.price * item.adminFee / 100)).toLocaleString()} pts
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 mt-3">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => updateQuantity(item.id, item.quantity - 1)}
                              className="w-8 h-8 flex items-center justify-center border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                              <i className="ri-subtract-line"></i>
                            </button>
                            <span className="w-12 text-center font-medium">{item.quantity}</span>
                            <button
                              onClick={() => updateQuantity(item.id, item.quantity + 1)}
                              className="w-8 h-8 flex items-center justify-center border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                              <i className="ri-add-line"></i>
                            </button>
                          </div>
                          <button
                            onClick={() => removeFromCart(item.id)}
                            className="ml-auto text-red-600 hover:text-red-700 transition-colors"
                          >
                            <i className="ri-delete-bin-line"></i> Remover
                          </button>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-bold text-gray-900">
                          {((item.price + Math.round(item.price * item.adminFee / 100)) * item.quantity).toLocaleString()} pts
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {cart.length > 0 && (
              <div className="p-6 border-t border-gray-200 bg-gray-50">
                <div className="space-y-2 mb-4">
                  <div className="flex justify-between text-gray-700">
                    <span>Subtotal:</span>
                    <span className="font-medium">{getCartTotal().toLocaleString()} pts</span>
                  </div>
                  <div className="flex justify-between text-orange-600">
                    <span>Taxa administrativa:</span>
                    <span className="font-medium">{getCartAdminFeeTotal().toLocaleString()} pts</span>
                  </div>
                  <div className="flex justify-between text-xl font-bold text-gray-900 pt-2 border-t border-gray-300">
                    <span>Total:</span>
                    <span className="text-green-600">{getCartGrandTotal().toLocaleString()} pts</span>
                  </div>
                </div>
                <button
                  onClick={handleCheckout}
                  className="w-full bg-green-600 hover:bg-green-700 text-white py-3 px-4 rounded-lg transition-colors font-medium"
                >
                  Finalizar Compra
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal de Checkout */}
      {showCheckout && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-xl font-bold text-gray-900">Finalizar Compra</h3>
            </div>

            {awaitingPayment ? (
              <div className="p-6 text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
                  <i className="ri-qr-code-line text-3xl text-blue-600"></i>
                </div>
                <h4 className="text-lg font-bold text-gray-900 mb-2">Aguardando Pagamento PIX</h4>
                <p className="text-gray-600 mb-6">
                  Escaneie o QR Code abaixo para realizar o pagamento
                </p>
                <div className="bg-gray-100 p-8 rounded-lg mb-6">
                  <div className="w-48 h-48 bg-white mx-auto flex items-center justify-center border-2 border-gray-300 rounded-lg">
                    <i className="ri-qr-code-line text-8xl text-gray-400"></i>
                  </div>
                </div>
                <p className="text-sm text-gray-500">
                  Redirecionando automaticamente após confirmação do pagamento...
                </p>
              </div>
            ) : (
              <>
                <div className="p-6">
                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-900 mb-3">Resumo do Pedido</h4>
                    <div className="space-y-2 text-sm">
                      {cart.map((item) => (
                        <div key={item.id} className="flex justify-between">
                          <span className="text-gray-600">
                            {item.name} x{item.quantity}
                          </span>
                          <span className="font-medium">
                            {((item.price + Math.round(item.price * item.adminFee / 100)) * item.quantity).toLocaleString()} pts
                          </span>
                        </div>
                      ))}
                      <div className="border-t border-gray-200 pt-2 flex justify-between font-bold text-base">
                        <span>Total:</span>
                        <span className="text-green-600">{getCartGrandTotal().toLocaleString()} pts</span>
                      </div>
                    </div>
                  </div>

                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-900 mb-3">Forma de Pagamento</h4>
                    <div className="space-y-2">
                      <label className="flex items-center p-4 border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer">
                        <input
                          type="radio"
                          name="paymentMethod"
                          value="pix"
                          checked={paymentMethod === 'pix'}
                          onChange={(e) => setPaymentMethod(e.target.value as any)}
                          className="mr-3"
                        />
                        <div className="flex items-center gap-3">
                          <i className="ri-qr-code-line text-2xl text-blue-600"></i>
                          <div>
                            <div className="font-medium text-gray-900">PIX</div>
                            <div className="text-sm text-gray-600">Pagamento instantâneo</div>
                          </div>
                        </div>
                      </label>
                      <label className="flex items-center p-4 border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer">
                        <input
                          type="radio"
                          name="paymentMethod"
                          value="boleto"
                          checked={paymentMethod === 'boleto'}
                          onChange={(e) => setPaymentMethod(e.target.value as any)}
                          className="mr-3"
                        />
                        <div className="flex items-center gap-3">
                          <i className="ri-barcode-line text-2xl text-orange-600"></i>
                          <div>
                            <div className="font-medium text-gray-900">Boleto Bancário</div>
                            <div className="text-sm text-gray-600">Vencimento em 3 dias úteis</div>
                          </div>
                        </div>
                      </label>
                    </div>
                  </div>
                </div>

                <div className="p-6 border-t border-gray-200 bg-gray-50">
                  <div className="flex gap-3">
                    <button
                      onClick={() => {
                        setShowCheckout(false);
                        setShowCart(true);
                      }}
                      className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Voltar
                    </button>
                    <button
                      onClick={confirmPurchase}
                      className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                    >
                      Confirmar Compra
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Modal de Relatório de Compras */}
      {showPurchaseReport && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-900">Relatório de Compras</h3>
                <button
                  onClick={() => setShowPurchaseReport(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {purchases.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-file-list-3-line text-3xl text-gray-400"></i>
                  </div>
                  <p className="text-gray-600">Nenhuma compra realizada</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {purchases.map((purchase) => (
                    <div key={purchase.id} className="border border-gray-200 rounded-lg overflow-hidden">
                      <div className="bg-gray-50 p-4 border-b border-gray-200">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-semibold text-gray-900">
                              Pedido #{purchase.id.toString().padStart(6, '0')}
                            </div>
                            <div className="text-sm text-gray-600">
                              {new Date(purchase.date).toLocaleDateString('pt-BR', {
                                day: '2-digit',
                                month: 'long',
                                year: 'numeric'
                              })}
                            </div>
                          </div>
                          <div className="text-right">
                            <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                              purchase.status === 'concluida' ? 'bg-green-100 text-green-800' :
                              purchase.status === 'processando' ? 'bg-blue-100 text-blue-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {purchase.status === 'concluida' ? 'Concluída' :
                               purchase.status === 'processando' ? 'Processando' :
                               'Cancelada'}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="p-4">
                        <div className="space-y-3 mb-4">
                          {purchase.items.map((item) => (
                            <div key={item.id} className="flex gap-4">
                              <img
                                src={item.image}
                                alt={item.name}
                                className="w-16 h-16 object-cover object-top rounded-lg"
                              />
                              <div className="flex-1">
                                <div className="font-medium text-gray-900">{item.name}</div>
                                <div className="text-sm text-gray-600">
                                  Quantidade: {item.quantity} | Valor unitário: {item.price.toLocaleString()} pts
                                </div>
                              </div>
                              <div className="text-right">
                                <div className="font-semibold text-gray-900">
                                  {(item.price * item.quantity).toLocaleString()} pts
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>

                        <div className="border-t border-gray-200 pt-4">
                          <div className="grid grid-cols-2 gap-4 text-sm mb-3">
                            <div>
                              <span className="text-gray-600">Nota Fiscal:</span>
                              <span className="ml-2 font-medium text-gray-900">{purchase.invoiceNumber}</span>
                            </div>
                            <div>
                              <span className="text-gray-600">Nota de Débito:</span>
                              <span className="ml-2 font-medium text-gray-900">{purchase.debitNoteNumber}</span>
                            </div>
                            <div>
                              <span className="text-gray-600">Forma de Pagamento:</span>
                              <span className="ml-2 font-medium text-gray-900">{purchase.paymentMethod}</span>
                            </div>
                            <div className="text-right">
                              <span className="text-gray-600">Total:</span>
                              <span className="ml-2 text-lg font-bold text-green-600">
                                {purchase.total.toLocaleString()} pts
                              </span>
                            </div>
                          </div>

                          <div className="flex gap-2">
                            <button className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm whitespace-nowrap cursor-pointer">
                              <i className="ri-download-line mr-2"></i>
                              Baixar NF
                            </button>
                            <button className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm whitespace-nowrap cursor-pointer">
                              <i className="ri-download-line mr-2"></i>
                              Baixar ND
                            </button>
                            <button className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm whitespace-nowrap cursor-pointer">
                              <i className="ri-printer-line mr-2"></i>
                              Imprimir
                            </button>
                            <button 
                              onClick={() => {
                                console.log('Clicou em Enviar Vouchers!', purchase);
                                setSelectedPurchase(purchase);
                                setShowVoucherSendModal(true);
                                setShowPurchaseReport(false);
                              }}
                              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm whitespace-nowrap cursor-pointer"
                            >
                              <i className="ri-send-plane-line mr-2"></i>
                              Enviar Vouchers
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal de Envio de Vouchers */}
      {showVoucherSendModal && selectedPurchase && (
        <VoucherSendModal
          purchase={selectedPurchase}
          onClose={() => {
            setShowVoucherSendModal(false);
            setSelectedPurchase(null);
            setShowPurchaseReport(true);
          }}
          onSuccess={() => {
            setShowVoucherSendModal(false);
            setShowVoucherSuccess(true);
            setTimeout(() => {
              setShowVoucherSuccess(false);
              setSelectedPurchase(null);
            }, 3000);
          }}
        />
      )}

      {/* Modal de Sucesso de Envio */}
      {showVoucherSuccess && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-check-line text-3xl text-green-600"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Vouchers Enviados!</h3>
            <p className="text-gray-600 mb-4">
              Os vouchers foram enviados com sucesso para todos os destinatários.
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-blue-800">
                <i className="ri-mail-line mr-2"></i>
                Enviado via Email
              </p>
              <p className="text-sm text-blue-800">
                <i className="ri-message-3-line mr-2"></i>
                Enviado via SMS
              </p>
              <p className="text-sm text-blue-800">
                <i className="ri-whatsapp-line mr-2"></i>
                Enviado via WhatsApp
              </p>
            </div>
            <button
              onClick={() => {
                setShowVoucherSuccess(false);
                setShowPurchaseReport(true);
              }}
              className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors whitespace-nowrap"
            >
              Fechar
            </button>
          </div>
        </div>
      )}
    </Layout>
  );
}
